import React, {useCallback, useEffect, useState} from 'react';
import {agentApi} from '../api';
import {GoLiveButton} from '../components/GoLiveButton';
import {ScheduleCallModal} from '../components/ScheduleCallModal';
import {ErrorBanner} from '../components/ui';
import {fmtDuration, toInitials} from '../shared';

const since = (iso) => {
  if (!iso) return '';
  const t = new Date(/Z|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`).getTime();
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)} h ago`;
  return `${Math.round(mins / 1440)} d ago`;
};

const Stat = ({label, value, tone, hint}) => (
    <div className={`ag-stat${tone ? ` ag-stat--${tone}` : ''}`}>
      <div className="ag-stat__value">{value ?? 0}</div>
      <div className="ag-stat__label">{label}</div>
      {hint && <div className="ag-stat__hint">{hint}</div>}
    </div>
);

function PresenceSwitch({online, busy, onChange, error}) {
  const label = !online ? 'Offline' : busy ? 'On a call' : 'Online';
  return (
      <div className={`ag-presence ag-presence--${!online ? 'off' : busy ? 'busy' : 'on'}`}>
        <div>
          <div className="ag-presence__state"><span className="ag-presence__dot" aria-hidden="true"/>{label}</div>
          <div className="ag-presence__hint">
            {!online ? 'Go online to receive live tour calls.' : busy ? "New calls won't ring you until this call ends."
                : 'Customers can call you now. Keep this tab open.'}
          </div>
          {error && <div className="ag-presence__error" role="alert">{error}</div>}
        </div>
        <button type="button" role="switch" aria-checked={online} className={`ag-switch${online ? ' ag-switch--on' : ''}`}
                onClick={() => onChange(!online)} disabled={busy}>
          <span className="ag-switch__knob"/>
          <span className="ag-sr">{online ? 'Go offline' : 'Go online'}</span>
        </button>
      </div>
  );
}

function MissedRow({call, onDone, onBook}) {
  const [noting, setNoting] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const done = async () => {
    setBusy(true);
    setError('');
    try {
      await agentApi.followedUp(call.requestId, note.trim());
      onDone(call.requestId);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };
  const mobile = (call.customerMobile || '').replace(/\D/g, '').slice(-10);
  return (
      <li className="ag-row">
        <div className="ag-avatar" aria-hidden="true">{toInitials(call.customerName)}</div>
        <div className="ag-row__body">
          <div className="ag-row__title">{call.customerName}</div>
          <div className="ag-row__meta">{call.propertyTitle || 'General enquiry'} · {since(call.requestedAt)}</div>
          {noting && (
              <div className="ag-note">
                <input className="ag-input" placeholder="What happened? e.g. Called, booked Saturday 11 am" value={note}
                       onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && done()} autoFocus/>
                <button type="button" className="ag-btn ag-btn--primary" onClick={done} disabled={busy}>Save</button>
                <button type="button" className="ag-btn" onClick={() => setNoting(false)}>Cancel</button>
              </div>
          )}
          {error && <div className="ag-error" role="alert">{error}</div>}
        </div>
        {!noting && (
            <div className="ag-row__actions">
              {mobile && <a className="ag-btn" href={`tel:+91${mobile}`}>Call</a>}
              {mobile && <a className="ag-btn" href={`https://wa.me/91${mobile}`} target="_blank" rel="noreferrer">WhatsApp</a>}
              <button type="button" className="ag-btn" onClick={() => onBook(call)}>Book a slot</button>
              <button type="button" className="ag-btn ag-btn--primary" onClick={() => setNoting(true)}>Followed up</button>
            </div>
        )}
      </li>
  );
}

/** The agent's home: online switch, today's numbers, customers waiting now, missed calls to follow up, next bookings. */
export function HomePage({agent, presence, waiting, onAccept, accepting, onOpenUpcoming, refreshKey}) {
  const [stats, setStats] = useState(null);
  const [missed, setMissed] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [error, setError] = useState('');
  const [booking, setBooking] = useState(null);
  const agentId = agent.agentId || agent.id;

  const load = useCallback(async () => {
    setError('');
    try {
      const [s, m, u] = await Promise.all([agentApi.stats(), agentApi.missed(), agentApi.upcoming()]);
      setStats(s);
      setMissed(m);
      setUpcoming(u);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  return (
      <div className="page ag-home">
        <div className="page-header">
          <h1 className="page-title">Hi {agent.name?.split(' ')[0] || 'there'}, <span>live tours</span></h1>
          <GoLiveButton agentId={agentId}/>
        </div>

        <PresenceSwitch online={presence.online} busy={presence.busy} onChange={presence.setOnline} error={presence.error}/>
        {error && <ErrorBanner message={error} onRetry={load}/>}

        <div className="ag-stats">
          <Stat label="Your calls today" value={stats?.today}/>
          <Stat label="Waiting now" value={waiting.length} tone={waiting.length ? 'orange' : ''}/>
          <Stat label="Missed, to follow up" value={missed.length} tone={missed.length ? 'red' : ''}/>
          <Stat label="Upcoming bookings" value={stats?.upcoming}/>
          <Stat label="Team online" value={stats ? `${stats.agentsFree}/${stats.agentsOnline}` : '–'} hint="free / online"/>
          <Stat label="Your average call" value={stats?.avgDurationSeconds ? fmtDuration(stats.avgDurationSeconds) : '–'}/>
        </div>

        {waiting.length > 0 && (
            <section className="content-card ag-card">
              <h2 className="ag-card__title">Waiting now</h2>
              <ul className="ag-list">
                {waiting.map((c) => (
                    <li key={c.requestId} className="ag-row">
                      <div className="ag-avatar ag-avatar--live" aria-hidden="true">{c.initials}</div>
                      <div className="ag-row__body">
                        <div className="ag-row__title">{c.name}</div>
                        <div className="ag-row__meta">{c.property} · waiting {since(c.requestedAt).replace(' ago', '')}</div>
                      </div>
                      <div className="ag-row__actions">
                        <button type="button" className="ag-btn ag-btn--accept" onClick={() => onAccept(c)}
                                disabled={accepting || presence.busy}>
                          {accepting ? 'Connecting…' : 'Accept'}
                        </button>
                      </div>
                    </li>
                ))}
              </ul>
            </section>
        )}

        <section className="content-card ag-card">
          <div className="ag-card__head">
            <h2 className="ag-card__title">Missed calls</h2>
            <span className="ag-card__sub">Customers nobody answered in the last 14 days</span>
          </div>
          {missed.length === 0 ? <p className="ag-empty">No missed calls. 🎉</p> : (
              <ul className="ag-list">
                {missed.map((c) => (
                    <MissedRow key={c.requestId} call={c} onBook={setBooking}
                               onDone={(id) => setMissed((list) => list.filter((x) => x.requestId !== id))}/>
                ))}
              </ul>
          )}
        </section>

        <section className="content-card ag-card">
          <div className="ag-card__head">
            <h2 className="ag-card__title">Next bookings</h2>
            <button type="button" className="ag-link" onClick={onOpenUpcoming}>All bookings →</button>
          </div>
          {upcoming.length === 0 ? <p className="ag-empty">No upcoming bookings.</p> : (
              <ul className="ag-list">
                {upcoming.slice(0, 4).map((c) => (
                    <li key={c.id} className="ag-row">
                      <div className="ag-avatar" aria-hidden="true">{toInitials(c.customerName)}</div>
                      <div className="ag-row__body">
                        <div className="ag-row__title">{c.customerName}</div>
                        <div className="ag-row__meta">{c.scheduledAtFormatted} · {c.propertyTitle}</div>
                      </div>
                      {!c.agentId && <span className="ag-tag">Unassigned</span>}
                    </li>
                ))}
              </ul>
          )}
        </section>

        {booking && (
            <ScheduleCallModal
                agentId={agentId}
                prefill={{name: booking.customerName, mobile: booking.customerMobile, email: booking.customerEmail,
                  propertyId: booking.propertyId}}
                onClose={() => setBooking(null)}
                onSaved={async () => {
                  const id = booking.requestId;
                  setBooking(null);
                  await agentApi.followedUp(id, 'Booked a slot').catch(() => {});
                  load();
                }}
            />
        )}
      </div>
  );
}
